import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Share2, Check, X } from "lucide-react";
import { EventTypeBadge } from "@/components/NewEventDialog";
import { formatDate, formatParties } from "@/lib/format";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";

// Lote 75 — "Compartilhamentos recebidos": compromissos que outro usuário do IApauta mandou
// direto para você. Aceitar coloca o compromisso na sua agenda (e no seu Google Calendar), já
// vinculado: se quem compartilhou remarcar ou cancelar, o seu acompanha. Recusar descarta.
export default function SharedInbox({ onChange }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => http.get("/compartilhamentos/recebidos").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  const answer = async (it, accept) => {
    setBusy(it.share_id);
    try {
      await http.post(`/compartilhamentos/${it.share_id}/${accept ? "aceitar" : "recusar"}`);
      toast.success(accept ? "Compromisso adicionado à sua agenda" : "Compartilhamento recusado");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao responder");
      load();
    } finally {
      setBusy(null);
    }
  };

  if (items.length === 0) return null;
  return (
    <section className="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4" data-testid="shared-inbox">
      <h2 className="font-display text-sm font-bold tracking-widest uppercase text-blue-700 mb-3 flex items-center gap-2">
        <Share2 className="w-4 h-4" strokeWidth={1.75} /> Compartilhamentos recebidos ({items.length})
      </h2>
      <ul className="space-y-2.5">
        {items.map((it) => {
          const h = it.hearing || {};
          return (
            <li key={it.share_id} data-testid={`shared-${it.share_id}`} className="rounded-md border border-blue-200 bg-white p-3">
              <div className="text-xs text-stone-500 mb-1.5">
                <strong className="text-stone-800">{it.from_name || "Um colega"}</strong> compartilhou com você
                {it.action ? <> — <em>{it.action}</em></> : null}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <EventTypeBadge hearing={h} />
                <span className="text-sm font-semibold text-stone-900 tabular-nums">
                  {formatDate(h.date)}{h.time ? ` · ${h.time}` : ""}
                </span>
              </div>
              <div className="text-sm text-stone-600 mt-0.5 break-words">
                {formatParties(h.parties) || h.case_number || h.description || "—"}
                {h.deadline_type ? ` · ${h.deadline_type}` : ""}
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" data-testid={`accept-${it.share_id}`} disabled={busy === it.share_id} onClick={() => answer(it, true)} className="gap-1.5 bg-stone-900 hover:bg-stone-800 text-white">
                  <Check className="w-4 h-4" strokeWidth={2} /> Aceitar
                </Button>
                <Button size="sm" variant="outline" data-testid={`decline-${it.share_id}`} disabled={busy === it.share_id} onClick={() => answer(it, false)} className="gap-1.5">
                  <X className="w-4 h-4" strokeWidth={2} /> Recusar
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
